/**
 * Status utility functions for mapping workflow_stage to display status.
 *
 * Part of 9.5f Status Field Consolidation: Hide status from UI and derive
 * display status from workflow_stage for user-facing needs.
 */

export const PROPOSAL_STAGES = ['proposal_draft', 'proposal_sent', 'proposal_declined'] as const;

export type DisplayStatus = 'Proposal' | 'Active' | 'On Hold' | 'Completed';

/**
 * Map workflow_stage to user-friendly display status.
 *
 * @param workflowStage - The workflow stage from the project (e.g., 'proposal_draft', 'in_progress')
 * @returns A user-friendly display status: 'Proposal', 'Active', 'On Hold', or 'Completed'
 */
export function getDisplayStatus(workflowStage: string | undefined | null): DisplayStatus {
  if (!workflowStage) {
    return 'Active';
  }
  if (PROPOSAL_STAGES.includes(workflowStage as typeof PROPOSAL_STAGES[number])) {
    return 'Proposal';
  }
  if (workflowStage === 'complete') {
    return 'Completed';
  }
  if (workflowStage === 'on_hold') {
    return 'On Hold';
  }
  return 'Active';
}

/**
 * Check if workflow_stage is a proposal stage.
 *
 * @param workflowStage - The workflow stage to check
 * @returns True if the stage is a proposal stage, False otherwise
 */
export function isProposalStage(workflowStage: string | undefined | null): boolean {
  if (!workflowStage) {
    return false;
  }
  return PROPOSAL_STAGES.includes(workflowStage as typeof PROPOSAL_STAGES[number]);
}
